<script setup lang="ts">
import { NButton, NPopconfirm } from 'naive-ui'

// A repo devcontainer lifecycle action button (start/stop/reboot/rebuild/…)
// that optionally warns before running if the repo currently has an active
// session — built once here (#844) so later destructive actions (#845
// reboot, #846 rebuild/rebuild-without-cache) can reuse the same
// confirm-if-active behavior without re-deriving the check.
const props = withDefaults(
  defineProps<{
    label: string
    slug: string
    testId: string
    loading?: boolean
    // Whether this action can interrupt an in-flight run at all.
    destructive?: boolean
    // Whether THIS repo's current state actually warrants the warning (i.e.
    // its devcontainer is running) — separate from `destructive` so a
    // destructive action on an idle/stopped repo skips the confirm step.
    // Destroy (#1235) sets this unconditionally: it's disruptive even stopped.
    warnActive?: boolean
    // Overrides the confirm dialog's body — defaults to the "session active"
    // copy every existing caller relies on. Destroy supplies its own, since
    // "a session is currently active" isn't true (or the point) when the
    // repo's already stopped.
    confirmMessage?: string
    buttonType?: 'default' | 'primary' | 'warning' | 'error'
    tertiary?: boolean
    // Set while ANOTHER lifecycle action is in flight for this repo (#1051).
    // The agent box runs them one at a time and 409s a second request, so the
    // button is blocked rather than letting the user queue a guaranteed error.
    disabled?: boolean
  }>(),
  {
    loading: false,
    destructive: false,
    warnActive: false,
    confirmMessage: undefined,
    buttonType: 'default',
    tertiary: false,
    disabled: false,
  },
)

const emit = defineEmits<{ run: [] }>()
</script>

<template>
  <NPopconfirm
    v-if="props.destructive && props.warnActive"
    positive-text="Continue"
    negative-text="Cancel"
    :positive-button-props="{ type: 'warning' }"
    @positive-click="emit('run')"
  >
    <template #trigger>
      <NButton
        size="small"
        :type="props.buttonType"
        :tertiary="props.tertiary"
        :loading="props.loading"
        :disabled="props.disabled"
        :data-testid="props.testId"
        >{{ props.label }}</NButton
      >
    </template>
    <template v-if="props.confirmMessage">{{ props.confirmMessage }}</template>
    <template v-else
      >A session is currently active in <strong>{{ props.slug }}</strong
      >'s devcontainer. Continue anyway?</template
    >
  </NPopconfirm>
  <NButton
    v-else
    size="small"
    :type="props.buttonType"
    :tertiary="props.tertiary"
    :loading="props.loading"
    :disabled="props.disabled"
    :data-testid="props.testId"
    @click="emit('run')"
    >{{ props.label }}</NButton
  >
</template>
